import type { DeletedExperimentSessionItem } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { createPaginationParams, createTableQueryOptions } from '@/components/table';
import { apiClient } from '@/utils/api';

const SESSIONS_BASE = '/sessions';

export type DeletedSessionsListParams = {
  page: number;
  per_page: number;
  search?: string;
  filters?: Record<string, string | undefined>;
  refresh?: number;
};

export const deletedSessionsListParamsAtom = atom<DeletedSessionsListParams>({
  ...createPaginationParams(),
  filters: {}
});

export const deletedSessionsQueryAtom = atomWithQuery<{
  success: boolean;
  message: string;
  data: DeletedExperimentSessionItem[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}>((get) => {
  const params = get(deletedSessionsListParamsAtom);

  return createTableQueryOptions({
    queryKey: ['deleted-sessions', params] as const,
    queryFn: async () => {
      const apiParams: Record<string, string | number> = {};

      if (params.page && params.page !== 1) {
        apiParams.page = params.page;
      }
      if (params.per_page && params.per_page !== 15) {
        apiParams.per_page = params.per_page;
      }

      if (params.search) {
        apiParams['filter[search]'] = params.search;
      }

      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) {
            apiParams[`filter[${key}]`] = value;
          }
        });
      }

      apiParams.only_trashed = 'true';

      const { data } = await apiClient.get(SESSIONS_BASE, { params: apiParams });

      return data as {
        success: boolean;
        message: string;
        data: DeletedExperimentSessionItem[];
        meta: {
          total: number;
          per_page: number;
          current_page: number;
          last_page: number;
        };
      };
    }
  });
});

export const restoreDeletedSession = async (publicId: string) => {
  const { data: response } = await apiClient.post(`${SESSIONS_BASE}/${publicId}/restore`);
  return response;
};
